
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State, toODataString } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable, of as observableOf, zip as observableZip, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

const cloneData = (data) => data.map(item => Object.assign({}, item));
@Injectable()
export abstract class EditService extends BehaviorSubject<GridDataResult> {

  protected http = inject(HttpClient);
  protected errorService = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  public selectChange: boolean = false;
  public changePending = new Subject<boolean>();
  public excludedPropertiesFromSave: Array<string> = [];

  private readComplete: Subject<any> = new Subject;
  protected data = new DataResult();
  protected originalData = new DataResult();
  protected createdItems: any[] = [];
  protected updatedItems: any[] = [];
  protected deletedItems: any[] = [];
  protected errors: any[];
  protected state: State;
  protected BASE_URL = `${this.environment.apiUrl}`;
  protected url = `${this.BASE_URL}${this.resource}`;
  protected queryString: string = '';

  constructor(
    private resource: string,
    private keys: Array<string>,
  ) {
    super(null);
  }

  public read(state: State, queryString = ''): Observable<boolean> {
    this.state = state;
    this.queryString = queryString;

    this.fetch().pipe(
      tap(data => { this.data = new DataResult(cloneData(data.value), data.total); }),
      tap(data => this.originalData = new DataResult(cloneData(data.value), data.total)),)
      .subscribe(data => { super.next(data); }, () => { }, () => { this.readComplete.next(true); });

    return this.readComplete;
  }

  public create(item: any): void {
    this.createdItems.push(item);
    this.data.unshift(item);
    super.next(this.data);
    this.changePending.next(true);
  }

  public update(item: any): void {
    this.changePending.next(true);
    if (!this.isNew(item)) {
      const index = this.itemIndex(item, this.updatedItems);
      if (index !== -1)
        this.updatedItems.splice(index, 1, item);
      else
        this.updatedItems.push(item);
    } else {
      const index = this.createdItems.indexOf(item);
      if (index !== -1)
        this.createdItems.splice(index, 1, item);
      else
        this.createdItems.push(item);
    }
  }

  public remove(item: any): void {
    let index = this.itemIndex(item, this.data.value);
    this.data.splice(index, 1);
    this.changePending.next(true);

    index = this.itemIndex(item, this.createdItems);
    if (index >= 0)
      this.createdItems.splice(index, 1);
    else
      this.deletedItems.push(item);

    index = this.itemIndex(item, this.updatedItems);
    if (index >= 0)
      this.updatedItems.splice(index, 1);

    super.next(this.data);
  }

  public isNew(item: any): boolean {
    return this.keys.every(x => !item[x] || item[x] === 0);
  }

  public hasChanges(): boolean {
    return Boolean(this.deletedItems.length || this.updatedItems.length || this.createdItems.length || this.selectChange);
  }

  public hasItems(): boolean {
    return Boolean(this.data.length);
  }

  public updateItem(item: any) {
    this.updatedItems.push(JSON.parse(JSON.stringify(item)));
    this.changePending.next(true);
  }

  public saveChanges(): void {
    if (!this.hasChanges()) return;

    const completed = [];

    this.deletedItems.forEach(item => {
      let uri = `${this.url}(${this.keys.map(key => `${key}=${item[key]}`).join(',')})`; // e.g. /odata/Orders(CustomerId=3,OrderId=7)
      completed.push(this.http.delete(uri));
    });

    this.updatedItems.forEach(item => {
      let uri = `${this.url}(${this.keys.map(key => `${key}=${item[key]}`).join(',')})`; // e.g. /odata/Orders(CustomerId=3,OrderId=7)
      completed.push(this.http.patch(uri, item));
    });

    this.createdItems.forEach(item => {
      let uri = `${this.url}`; // e.g. /odata/Orders
      completed.push(this.http.post(uri, item));
    });

    this.reset();

    observableZip(...completed).subscribe((response) => {
      this.read(this.state, this.queryString);
    },
      (error) => this.errorService.error('Error Saving to:' + this.url, error));
  }

  public saveChanges$(): Observable<any> {
    if (!this.hasChanges()) return observableOf(false);

    const completed = [];

    this.deletedItems.forEach(item => {
      let uri = `${this.url}(${this.keys.map(key => `${key}=${item[key]}`).join(',')})`; // e.g. /odata/Orders?CustomerId=3&OrderId=7
      completed.push(this.http.delete(uri));
    });

    this.updatedItems.forEach(item => {
      this.excludedPropertiesFromSave.forEach(p => {
        delete item[p];
      });

      let uri = `${this.url}(${this.keys.map(key => `${key}=${item[key]}`).join(',')})`; // e.g. /odata/Orders(CustomerId=3,OrderId=7)
      completed.push(this.http.patch(uri, item));
    });

    this.createdItems.forEach(item => {
      let uri = `${this.url}`; // e.g. /odata/Orders
      completed.push(this.http.post(uri, item));
    });

    this.reset();

    return observableZip(...completed).pipe(map((response) => {
      this.read(this.state, this.queryString); return response;
    }));
  }

  public cancelChanges(): void {
    this.reset();
    this.data = this.originalData;
    this.originalData = new DataResult(cloneData(this.originalData.value), this.originalData.total);
    super.next(this.data);
  }

  public assignValues(target: any, source: any): void {
    Object.assign(target, source);
  }

  protected reset() {
    this.data = new DataResult();
    this.changePending.next(false);
    this.deletedItems = [];
    this.updatedItems = [];
    this.createdItems = [];
  }

  private fetch(): Observable<DataResult> {
    return this.http
      .get(`${this.url}?${this.oDataQueryString()}`).pipe(
        map((response) => {
          let data = (response as any).value;
          let total = parseInt(response['@odata.count'], 10);
          return new DataResult(data, total);
        }));
  }

  private oDataQueryString(): string {
    if (this.queryString.search('filter') !== -1 && this.state.filter && this.state.filter.filters.length > 0) {
      let newState: State = {};
      let filterState: State = {};

      this.assignValues(newState, this.state);
      newState.filter = null;
      filterState.filter = this.state.filter;

      let pagingQueryString = toODataString(newState);
      let newFilterQueryString = toODataString(filterState);
      let orString = ' or ';
      let newFilterString = this.queryString.replace('$filter=', newFilterQueryString + ' and ');

      if (this.queryString.search(orString) !== -1) {
        let orIndexes = this.stringIndexes(this.queryString, orString);
        let newFormatedQueryString = '';
        orIndexes.splice(0, 0, 0);

        orIndexes.map((idx, i) => {
          let startPosition = idx;
          let endPosition = orIndexes[i + 1];
          if (idx !== 0) startPosition = idx + orString.length;

          let criteriaString = this.queryString.substring(startPosition, endPosition);
          let targetPosition = criteriaString.lastIndexOf(')') + startPosition;
          let newCriteriaString = this.queryString.substring(startPosition, targetPosition) + ' ' + newFilterQueryString.replace('$filter=', 'and ') + ' )';
          this.queryString.replace(criteriaString, newCriteriaString);

          if (i !== 0) {
            newFormatedQueryString += ' or ' + newCriteriaString;
          } else {
            newFormatedQueryString += ' ' + newCriteriaString;
          }
        });

        newFilterString = newFormatedQueryString;
      }

      return `${pagingQueryString}&$count=true${newFilterString}`;
    } else {
      return `${toODataString(this.state)}&$count=true${this.queryString}`;
    }
  }

  itemIndex = (item: any, data: any[]): number => {
    for (let idx = 0; idx < data.length; idx++) {
      if (this.keys.every(key => data[idx][key] === item[key])) {
        return idx;
      }
    }
    return -1;
  }

  stringIndexes = (source, find): number[] => {
    let result = [];
    for (let i = 0; i < source.length; ++i) {
      if (source.substring(i, i + find.length) === find) {
        result.push(i);
      }
    }
    return result;
  }
}

class DataResult implements GridDataResult {
  data = [];
  total = 0;

  constructor(data?: any[], total?: number) {
    this.data = data || [];
    this.total = total || 0;
  }
  unshift = (item) => {
    this.data.unshift(item); this.total++;
  }
  splice = (index, item) => {
    this.data.splice(index, item); this.total--;
  }
  get length() { return this.data.length; }
  map = (x) => this.data.map(x);
  get value() { return this.data; }
}